import React, { useState, useEffect } from 'react';
import {
  Modal,
  Textarea,
  Select,
  MultiSelect,
  Button,
  Group,
  Input,
  SegmentedControl,
  Checkbox,
  Autocomplete,
  Text,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { useTranslation } from 'react-i18next';
import { Competency, Grade, Swimlane } from '../../types';
import { createLabelWithLink } from '../../utils/labelWithLink';
import { createEnterKeyHandler } from '../../utils/enterKeyHandler';
import {
  useUsers,
  useCreateCompetency,
  useUpdateCompetency,
  useRemoveCompetencyFromMap,
  useGenerateCompetencyDescription,
  useCompetencies,
} from '../../hooks/useApi';
import { useTags } from '../../hooks/useReferenceApi';
import { showToast } from '../../utils/toast';
import { ExistingCompetenciesSelector } from './ExistingCompetenciesSelector';

interface CompetencyCreateModalProps {
  opened: boolean;
  onClose: () => void;
  swimlanes: Swimlane[];
  grades: Grade[];
  editingCompetency?: Competency | null;
  competencyMapId?: string;
  onSubmit?: (competencyData: {
    title: string;
    description: string;
    swimlaneId?: string;
    gradeId?: string;
    tags: string[];
    mentors: string[];
    isRequired?: boolean;
    domain?: string;
  }) => void;
  onRemoveFromMap?: () => void;
  // Новые пропсы для управления видом модала
  hideSwimlaneAndGrade?: boolean; // Скрыть поля свимлейна и грейда
  hideModeSelection?: boolean; // Скрыть выбор режима (создать/выбрать)
  hideRequiredField?: boolean; // Скрыть поле "обязательная компетенция"
}

export const CompetencyCreateModal: React.FC<CompetencyCreateModalProps> = ({
  opened,
  onClose,
  swimlanes,
  grades,
  editingCompetency,
  competencyMapId,
  onSubmit,
  onRemoveFromMap,
  hideSwimlaneAndGrade = false,
  hideModeSelection = false,
  hideRequiredField = false,
}) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'create' | 'select'>('create');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [swimlane, setSwimlane] = useState<string | null>(null);
  const [grade, setGrade] = useState<string | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  const [mentors, setMentors] = useState<string[]>([]);
  const [domain, setDomain] = useState<string>('technical');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRequired, setIsRequired] = useState(false);
  const [selectedExistingCompetency, setSelectedExistingCompetency] = useState<Competency | null>(
    null,
  );

  // React Query хуки
  const { data: mentorOptions = [], isLoading: loadingMentors } = useUsers();
  const { data: allCompetencies = [] } = useCompetencies();
  const { data: tagDict } = useTags();
  const createCompetency = useCreateCompetency();
  const updateCompetency = useUpdateCompetency();
  const removeFromMap = useRemoveCompetencyFromMap();
  const generateDescription = useGenerateCompetencyDescription();

  // Заполняем форму данными для редактирования
  useEffect(() => {
    if (opened) {
      // Каждое открытие — с формы: выбор существующей включается только переключателем.
      setMode('create');
    }

    if (editingCompetency) {
      setName(editingCompetency.title);
      setDescription(editingCompetency.description || '');
      setSwimlane(editingCompetency.swimlaneId);
      setTags((editingCompetency.tags || []).map((tg) => tg._id));

      // Убеждаемся что mentors - это массив строк (извлекаем только ID)
      const mentorsArray = Array.isArray(editingCompetency.mentors)
        ? editingCompetency.mentors.map((mentor) =>
            typeof mentor === 'string' ? mentor : mentor._id,
          )
        : [];
      setMentors(mentorsArray);
      setDomain(editingCompetency.domain || 'technical');

      // Находим грейд, в котором находится компетенция
      const findGradeForCompetency = () => {
        for (const grade of grades) {
          const hasCompetency = grade.competencies?.some(
            (comp) => comp._id === editingCompetency._id,
          );
          if (hasCompetency) {
            return grade._id;
          }
        }
        return null;
      };

      const gradeId = findGradeForCompetency();
      setGrade(gradeId);
      setIsRequired(editingCompetency.isRequired || false);
      setSelectedExistingCompetency(null);
    } else {
      // Сброс формы при создании новой компетенции
      setName('');
      setDescription('');
      setSwimlane(null);
      setGrade(null);
      setTags([]);
      setMentors([]);
      setDomain('technical');
      setIsRequired(false);
      setSelectedExistingCompetency(null);
    }
  }, [editingCompetency, grades, opened]);

  // Фильтруем компетенции для автодополнения
  const getCompetencySuggestions = () => {
    if (!name.trim()) return [];

    const filtered = allCompetencies
      .filter((competency) => competency.title.toLowerCase().includes(name.toLowerCase()))
      .slice(0, 5) // Ограничиваем до 5 результатов
      .map((competency) => competency.title);

    return filtered;
  };

  // Обработка выбора из списка автодополнения
  const handleCompetencySelect = (selectedValue: string) => {
    const selectedCompetency = allCompetencies.find((comp) => comp.title === selectedValue);
    // В справочнике и при правке режима «выбрать существующую» нет — остаёмся в форме.
    const canSelectExisting = !editingCompetency && !hideModeSelection;
    if (selectedCompetency && canSelectExisting) {
      // Переключаемся в режим выбора существующей компетенции
      setMode('select');
      setSelectedExistingCompetency(selectedCompetency);
    }
  };

  // Убрать из карты — с подтверждением: связь с грейдом и дорожкой не вернуть кнопкой «Отмена».
  const handleRemoveFromMap = () => {
    if (!editingCompetency || !competencyMapId) return;
    modals.openConfirmModal({
      title: t('competencyMaps.removeFromMap'),
      centered: true,
      children: (
        <Text size="sm">
          {t('competencyMaps.removeFromMapConfirm', { name: editingCompetency.title })}
        </Text>
      ),
      labels: { confirm: t('competencyMaps.removeFromMap'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: removeFromMapConfirmed,
    });
  };

  const removeFromMapConfirmed = async () => {
    try {
      if (!editingCompetency || !competencyMapId) return;
      await removeFromMap.mutateAsync({
        competencyId: editingCompetency._id,
        mapId: competencyMapId,
      });

      showToast({
        title: t('common.success'),
        message: t('competencyMaps.competencyRemovedFromMap'),
        type: 'success',
      });

      onClose();
      onRemoveFromMap?.();
    } catch (error) {
      console.error('Error removing competency from map:', error);
      showToast({
        title: t('common.error'),
        message: t('competencyMaps.competencyRemoveFromMapError'),
        type: 'error',
      });
    }
  };

  // Те же поля, что помечены звёздочкой: кнопка не нажимается, пока они пусты.
  const isFormValid = !!name && !!description && (hideSwimlaneAndGrade || (!!swimlane && !!grade));

  const handleSubmit = async () => {
    try {
      if (!isFormValid) return;

      const competencyData = {
        title: name,
        description,
        ...(hideSwimlaneAndGrade
          ? {}
          : { swimlaneId: swimlane || undefined, gradeId: grade || undefined }),
        tags,
        mentors,
        domain,
        ...(hideRequiredField ? {} : { isRequired }),
        // Бэкенд привязывает компетенцию только к этой карте (грейды и дорожки общие).
        ...(competencyMapId ? { competencyMapId } : {}),
      };

      if (editingCompetency) {
        // Обновление компетенции
        await updateCompetency.mutateAsync({
          _id: editingCompetency._id,
          data: {
            ...competencyData,
            gradeId: grade || undefined, // Добавляем gradeId для обновления
          },
        });

        // Показываем toast об успешном обновлении
        showToast({
          title: t('common.success'),
          message: t('competencyMaps.competencyUpdated'),
          color: 'teal',
        });
      } else {
        // Создание новой компетенции
        await createCompetency.mutateAsync(competencyData);

        // Показываем toast об успешном создании
        showToast({
          title: t('common.success'),
          message: t('competencyMaps.competencyCreated'),
          color: 'teal',
        });
      }

      // Вызываем внешний onSubmit если он передан
      if (onSubmit) {
        onSubmit(competencyData);
      }

      onClose();
    } catch (error) {
      console.error('Ошибка при сохранении компетенции:', error);

      // Показываем toast об ошибке
      showToast({
        title: t('common.error'),
        message: t('competencyMaps.competencySaveError'),
        color: 'red',
      });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        editingCompetency ? t('competencyMaps.editCompetency') : t('competencyMaps.addCompetency')
      }
      size="lg"
      centered
      onKeyDown={createEnterKeyHandler({ onSubmit: handleSubmit })}
    >
      <div style={{ padding: '20px 0' }}>
        {!editingCompetency && !hideModeSelection && (
          <SegmentedControl
            value={mode}
            onChange={(value) => setMode(value as 'create' | 'select')}
            data={[
              { label: t('competencyMaps.createNew'), value: 'create' },
              { label: t('competencyMaps.selectExisting'), value: 'select' },
            ]}
            mb="lg"
            fullWidth
          />
        )}

        {mode === 'create' ? (
          <>
            <Autocomplete
              label={t('competencyMaps.competencyName')}
              placeholder={t('competencyMaps.competencyName')}
              required
              mb="md"
              value={name}
              onChange={setName}
              data={getCompetencySuggestions()}
              onOptionSubmit={handleCompetencySelect}
              filter={({ options, search }) => options}
            />
            <Input.Wrapper
              labelElement="div"
              withAsterisk
              {...createLabelWithLink({
                labelText: t('competencyMaps.competencyDescription'),
                linkText: isGenerating ? t('common.generating') : t('common.generate'),
                onLinkClick: async (e) => {
                  e.preventDefault();

                  if (!name.trim()) {
                    showToast({
                      title: t('common.error'),
                      message: t('competencyMaps.generateDescriptionError'),
                      color: 'red',
                    });
                    return;
                  }

                  try {
                    setIsGenerating(true);
                    setDescription(''); // Очищаем описание перед началом генерации

                    // Получаем названия грейда и свимлейна по их ID
                    const selectedGrade = grades.find((g) => g._id === grade);
                    const selectedSwimlane = swimlanes.find((s) => s._id === swimlane);

                    // Текст приходит целиком: ai-service отдаёт один ответ,
                    // потокового режима у него нет. Пока ждём — «Генерируем…».
                    const { description: generated } = await generateDescription.mutateAsync({
                      competencyName: name,
                      gradeName: selectedGrade?.title,
                      swimlaneName: selectedSwimlane?.title,
                      competencyMapId,
                    });
                    setDescription(generated);
                    showToast({
                      title: t('common.success'),
                      message: t('competencyMaps.descriptionGenerated'),
                      color: 'teal',
                    });
                  } catch (error: unknown) {
                    console.error('Ошибка генерации описания:', error);
                    showToast({
                      title: t('common.error'),
                      message:
                        error instanceof Error
                          ? error.message
                          : t('competencyMaps.generateDescriptionError'),
                      color: 'red',
                    });
                  } finally {
                    setIsGenerating(false);
                  }
                },
              })}
              mb="md"
            >
              <Textarea
                placeholder={t('competencyMaps.competencyDescription')}
                minRows={5}
                rows={5}
                value={description}
                onChange={(e) => setDescription(e.currentTarget.value)}
              />
            </Input.Wrapper>
            {!hideSwimlaneAndGrade && (
              <>
                <Select
                  label={t('competencyMaps.swimlane')}
                  placeholder={t('competencyMaps.swimlane')}
                  data={swimlanes.map((s) => ({ value: s._id, label: s.title }))}
                  value={swimlane}
                  onChange={setSwimlane}
                  required={!hideSwimlaneAndGrade}
                  mb="md"
                />
                <Select
                  label={t('competencyMaps.grade')}
                  placeholder={t('competencyMaps.selectGrade')}
                  data={grades.map((g) => ({ value: g._id, label: g.title }))}
                  value={grade}
                  onChange={setGrade}
                  required={!hideSwimlaneAndGrade}
                  mb="md"
                />
              </>
            )}
            <MultiSelect
              label={t('competencyMaps.tags')}
              placeholder={t('competencyMaps.tags')}
              data={(tagDict || []).map((tg) => ({ value: tg._id, label: tg.name }))}
              value={tags}
              onChange={setTags}
              searchable
              mb="md"
            />
            <Select
              label={t('competencyMaps.domain')}
              description={t('competencyMaps.domainHint')}
              data={['technical', 'qa', 'devops', 'product', 'soft'].map((d) => ({
                value: d,
                label: t(`competencyMaps.domains.${d}`),
              }))}
              value={domain}
              onChange={(v) => setDomain(v || 'technical')}
              allowDeselect={false}
              mb="md"
            />
            <MultiSelect
              label={t('competencyMaps.mentors')}
              placeholder={loadingMentors ? t('common.loading') : t('competencyMaps.selectMentors')}
              // Уволенных (архив) в менторы не добавить; уже назначенные остаются видны.
              data={mentorOptions
                .filter((user) => user.active !== false || mentors.includes(user._id))
                .map((user) => ({
                  value: user._id,
                  label: `${user.name} (${user.position || 'Сотрудник'})`,
                }))}
              value={mentors}
              onChange={setMentors}
              searchable
              disabled={loadingMentors}
              mb="md"
            />
            {!hideRequiredField && (
              <Checkbox
                label={t('competencyMaps.isRequired')}
                checked={isRequired}
                onChange={(event) => setIsRequired(event.currentTarget.checked)}
                mb="xl"
              />
            )}
            <Group justify="space-between" gap="sm">
              {editingCompetency && competencyMapId && (
                <Button
                  variant="light"
                  color="red"
                  onClick={handleRemoveFromMap}
                  loading={removeFromMap.isPending}
                >
                  {t('competencyMaps.removeFromMap')}
                </Button>
              )}
              <Group gap="sm">
                <Button variant="light" color="gray" onClick={onClose}>
                  {t('common.cancel')}
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={!isFormValid}
                  loading={createCompetency.isPending || updateCompetency.isPending}
                >
                  {editingCompetency ? t('common.save') : t('common.add')}
                </Button>
              </Group>
            </Group>
          </>
        ) : (
          <ExistingCompetenciesSelector
            competencyMapId={competencyMapId || ''}
            grades={grades}
            swimlanes={swimlanes}
            onClose={onClose}
            preselectedCompetency={selectedExistingCompetency}
            onSuccess={() => {
              // Обновляем родительский компонент
              onSubmit?.({
                title: '',
                description: '',
                swimlaneId: '',
                gradeId: '',
                tags: [],
                mentors: [],
              });
            }}
          />
        )}
      </div>
    </Modal>
  );
};
